import WorkImage from "../components/WorkImage";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, Camera, Check, ChevronDown, Globe, Instagram, Lightbulb,
  PhoneCall, Search, SearchCheck, Sprout, Star, Target, TrendingUp,
  UtensilsCrossed, ClipboardList, Rocket, Hotel,
} from "lucide-react";
import {
  FAQS, IMG, INDUSTRIES, INSTA_POSTS, INSTAGRAM_URL, SERVICES, SERVICE_CATEGORIES, WORK_PROJECTS, waLink,
} from "../data/content";
import { EASE, FadeUp, MaskedLine, Overline } from "../components/Reveal";
import { INDUSTRY_ICONS } from "../components/serviceIcons";

/* Slide-in on scroll helper */
const Slide = ({ children, x = 0, y = 28, delay = 0, className = "" }) => (
  <motion.div
    className={`slide-block ${className}`}
    initial={{ opacity: 0, x, y }}
    whileInView={{ opacity: 1, x: 0, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.85, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

/* ---------------- Hero: content left, photo collage right ---------------- */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yColA = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const yColB = useTransform(scrollYProgress, [0, 1], [0, -70]);

  const PhotoCard = ({ src, alt, label, className = "", delay = 0 }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.92, y: 24 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.8, delay, ease: EASE }}
      className="group relative rounded-3xl overflow-hidden border-4 border-white shadow-[0_24px_60px_rgba(59,35,115,0.22)] bg-white"
      data-testid={`hero-collage-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
    >
      <div className={`relative overflow-hidden ${className}`}>
        <img src={src} alt={alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
      </div>
      <span className="flex items-center gap-1.5 px-3.5 py-2.5 text-[10px] font-mono uppercase tracking-[0.16em] font-semibold text-royal-deep">
        <span className="w-1.5 h-1.5 rounded-full bg-saffron shrink-0" />
        {label}
      </span>
    </motion.div>
  );

  return (
    <section ref={ref} className="hospitality-background relative overflow-hidden  pt-32 pb-20">
      <div className="absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-royal-soft blur-3xl" aria-hidden />
      <div className="absolute bottom-0 -left-32 w-96 h-96 rounded-full bg-saffron-soft blur-3xl opacity-70" aria-hidden />
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 relative grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
        <div className="lg:col-span-6">
          <FadeUp y={14}>
            <span
              data-testid="hero-overline-badge"
              className="hero-brand-signature"
            >
              <span className="hero-brand-mark" aria-hidden="true"><TrendingUp size={25} strokeWidth={2.2} /></span>
              <span className="hero-brand-copy">
                <span className="hero-brand-name">GrowMyRestro<span className="hero-brand-dot">.</span></span>
                <span className="hero-brand-tagline">Your hospitality growth partner</span>
              </span>
            </span>
          </FadeUp>
          <h1 className="mt-6 font-display font-extrabold tracking-tight leading-[1.06] text-4xl sm:text-5xl lg:text-[3.8rem] text-royal-deep">
            <MaskedLine delay={0.15}>360° Hospitality Growth</MaskedLine>
            <MaskedLine delay={0.3}>&amp; Business</MaskedLine>
            <MaskedLine delay={0.45} className="text-saffron">Solutions.</MaskedLine>
          </h1>
          <FadeUp delay={0.55}>
            <p className="mt-5 font-display font-bold text-lg text-royal">Everything your hospitality business needs, under one roof.</p>
            <p className="mt-3 text-base text-ink-muted leading-relaxed max-w-xl">
              We help restaurants, cafés, hotels, resorts, pubs, cloud kitchens and other hospitality businesses grow
              through digital marketing, branding, technology, content and complete hospitality business solutions —
              from getting more customers online to professional spaces, events, uniforms and branded products.
            </p>
          </FadeUp>
          <FadeUp delay={0.7} className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to="/contact"
              data-testid="hero-audit-cta-button"
              className="group inline-flex items-center gap-2 bg-royal text-white font-bold px-7 py-3.5 rounded-full hover:bg-saffron hover:text-royal-deep transition-colors duration-300"
            >
              Get a Free Consultation
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-explore-services-button"
              className="inline-flex items-center gap-2 border-2 border-royal/25 text-royal-deep font-bold px-7 py-3.5 rounded-full hover:border-saffron hover:text-saffron transition-colors duration-300"
            >
              Explore Our Services
            </Link>
          </FadeUp>
        </div>

        <div className="lg:col-span-6 relative" data-testid="hero-collage">
          <div className="grid grid-cols-2 gap-4 sm:gap-5">
            <motion.div style={{ y: yColA }} className="space-y-4 sm:space-y-5 pt-10">
              <PhotoCard src="/photo-marketing.jpg" alt="Digital marketing strategy for a restaurant" label="Digital Growth" className="aspect-[4/3]" delay={0.35} />
              <PhotoCard src="/content-camera.png" alt="Photographer capturing food content with a professional camera" label="Content" className="aspect-square" delay={0.5} />
            </motion.div>
            <motion.div style={{ y: yColB }} className="space-y-4 sm:space-y-5">
              <PhotoCard src="/photo-uniforms.jpg" alt="Restaurant team in branded uniforms" label="Hospitality Solutions" className="aspect-[4/5]" delay={0.65} />
              <PhotoCard src="/photo-bottles.jpg" alt="Customized branded water bottles" label="Branding" className="aspect-[4/3]" delay={0.8} />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Solutions strip (plain-words marquee) ---------------- */
const IconStrip = () => {
  const items = [
    { photo: "/photo-gmb.jpg", label: "Get Found on Google", sub: "SEO & Google Maps", to: "/services/seo" },
    { photo: "/photo-social-phone.jpg", label: "Instagram & Facebook", sub: "Posts, reels & growth", to: "/services/social-media-marketing" },
    { photo: "/photo-social-team.jpg", label: "Ads That Bring Guests", sub: "Targeted promotions", to: "/services/meta-ads" },
    { photo: IMG.webDesign, label: "Your Own Website", sub: "Design & development", to: "/services/website-design" },
    { photo: "/photo-food-shoot.jpg", label: "Food Photos & Videos", sub: "That make people hungry", to: "/services/food-photography" },
    { photo: "/photo-uniforms.jpg", label: "Events, Uniforms & More", sub: "Hospitality solutions", to: "/services" },
  ];
  const row = (key) => (
    <div key={key} className="flex shrink-0">
      {items.map((it, i) => (
        <Link
          key={`${key}-${i}`}
          to={it.to}
          data-testid={`strip-item-${key}-${i}`}
          className="hospitality-card group mx-3 flex items-center gap-4 bg-white border border-line rounded-2xl px-4 py-4 w-80 shrink-0 hover:border-saffron hover:shadow-[0_16px_40px_rgba(59,35,115,0.12)] hover:-translate-y-1 transition-all duration-300"
        >
          <span className="w-16 h-16 rounded-xl overflow-hidden bg-royal-soft shrink-0">
            <img src={it.photo} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
          </span>
          <span>
            <span className="block font-display font-bold text-royal-deep leading-tight">{it.label}</span>
            <span className="block text-xs text-ink-muted mt-0.5">{it.sub}</span>
          </span>
          <ArrowRight className="w-4 h-4 ml-auto text-line transition-all duration-300 group-hover:text-saffron group-hover:translate-x-1 shrink-0" />
        </Link>
      ))}
    </div>
  );
  return (
    <section className="hospitality-background border-y border-line py-10 overflow-hidden" data-testid="icon-strip">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 mb-7 flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <Slide x={-24} y={0}>
          <p className="font-display font-extrabold text-royal-deep text-xl leading-tight">
            One Partner. <span className="text-saffron">Multiple Hospitality Solutions.</span>
          </p>
        </Slide>
        <p className="text-xs font-mono uppercase tracking-[0.18em] text-ink-muted">Hover to pause · tap a card to explore</p>
      </div>
      <div className="overflow-hidden">
        <div className="flex w-max animate-marquee hover:[animation-play-state:paused]">
          {row("a")}
          {row("b")}
        </div>
      </div>
    </section>
  );
};

/* ---------------- More Than Just Marketing ---------------- */
const MoreThanMarketing = () => {
  const pillars = [
    { Icon: Search, title: "Get Found", desc: "Improve your visibility on Google and social media." },
    { Icon: Camera, title: "Get Noticed", desc: "Showcase your food, brand and experience with professional content." },
    { Icon: Target, title: "Get Customers", desc: "Use marketing, SEO and advertising to reach your target audience." },
    { Icon: Lightbulb, title: "Build Better", desc: "Create better websites, interiors, events, uniforms and branded products." },
  ];
  return (
    <section className="hospitality-background py-12 sm:py-16 lg:py-20  relative overflow-hidden" data-testid="more-than-marketing-section">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <Slide x={-40} y={0} className="relative self-stretch">
          <div className="rounded-[2.5rem] overflow-hidden shadow-[0_30px_70px_rgba(59,35,115,0.18)] aspect-[4/3] lg:aspect-auto lg:h-full lg:min-h-[420px]">
            <img src="https://images.unsplash.com/photo-1782983595362-f42243ecb2fc?auto=format&fit=crop&w=1400&q=85" alt="Bright restaurant dining room with set tables and floor-to-ceiling garden windows" className="w-full h-full object-cover" loading="lazy" />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
            className="absolute bottom-4 right-4 w-28 sm:w-36 rounded-2xl overflow-hidden border-4 border-white shadow-xl rotate-2"
            data-testid="mtm-gmb-card"
          >
            <img src="/photo-gmb.jpg" alt="Restaurant found on Google Maps" className="w-full aspect-[4/5] object-cover" />
            <span className="absolute bottom-0 inset-x-0 bg-royal-deep/90 text-cream text-[10px] font-mono uppercase tracking-[0.14em] text-center py-2">Get found on Google</span>
          </motion.div>
        </Slide>
        <div>
          <Slide x={40} y={0}>
            <Overline>More Than Just Marketing</Overline>
            <h2 className="mt-4 font-display font-extrabold tracking-tight leading-[1.12] text-3xl sm:text-4xl lg:text-[2.9rem] text-royal-deep">
              Running a hospitality business means <span className="text-saffron">managing many things at once</span>
            </h2>
            <p className="mt-5 text-ink-muted leading-relaxed">
              You need customers to find you online. You need great content to attract them. You need a strong brand to
              make them remember you. You need a professional space and team to create the right experience.{" "}
              <span className="font-bold text-royal">That's where GrowMyRestro comes in</span> — we combine digital
              growth services and practical hospitality business solutions under one roof.
            </p>
          </Slide>
          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            {pillars.map((p, i) => (
              <Slide key={p.title} y={24} delay={0.15 + i * 0.08}>
                <div className="hospitality-card group h-full bg-white rounded-3xl border border-line p-5 hover:border-royal/30 hover:shadow-[0_18px_45px_rgba(59,35,115,0.12)] hover:-translate-y-1 transition-all duration-300" data-testid={`pillar-${i}`}>
                  <span className="w-11 h-11 rounded-full bg-royal flex items-center justify-center transition-colors duration-300 group-hover:bg-saffron">
                    <p.Icon className="w-5 h-5 text-white transition-colors duration-300 group-hover:text-royal-deep" />
                  </span>
                  <h3 className="mt-3.5 font-display font-bold text-lg text-royal-deep">{p.title}</h3>
                  <p className="mt-1.5 text-[13px] text-ink-muted leading-relaxed">{p.desc}</p>
                </div>
              </Slide>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- What We Do (violet band) ---------------- */
const catIcons = { growth: TrendingUp, creative: Lightbulb, execution: UtensilsCrossed };

const ScrollSlideIn = ({ index, progress, children }) => {
  const x = useTransform(progress, [0, 1], [-(170 + index * 90), 0]);
  const opacity = useTransform(progress, [0, 0.5 + index * 0.15, 1], [0, 0.35, 1]);
  return (
    <motion.div style={{ x, opacity }} className="h-full">
      {children}
    </motion.div>
  );
};

const WhatWeDo = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 90%", "start 30%"] });
  return (
    <section ref={ref} className="py-12 sm:py-16 lg:py-20 bg-royal-deep relative overflow-hidden" data-testid="what-we-do-section">
      <img src="/photo-social-team.jpg" alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-20" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-b from-royal-deep/90 via-royal-deep/80 to-royal-deep/95" aria-hidden />
      <div className="absolute -top-32 left-1/4 w-[28rem] h-[28rem] rounded-full bg-royal/40 blur-3xl" aria-hidden />
      <div className="absolute -bottom-24 right-0 w-96 h-96 rounded-full bg-saffron/15 blur-3xl" aria-hidden />
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 relative">
        <FadeUp className="text-center">
          <h2 className="font-display font-extrabold tracking-tight leading-[1.12] text-3xl sm:text-4xl lg:text-5xl text-white">
            What <span className="text-saffron">We Do</span>
          </h2>
          <p className="mt-4 text-white/70 max-w-2xl mx-auto leading-relaxed">
            Online growth. Creative content. Real-world execution — three practices, one partner.
          </p>
        </FadeUp>
        <div className="grid lg:grid-cols-3 gap-6 mt-8 sm:mt-10">
          {SERVICE_CATEGORIES.map((cat, ci) => {
            const Icon = catIcons[cat.id];
            return (
              <ScrollSlideIn key={cat.id} index={ci} progress={scrollYProgress}>
                <div className="group h-full bg-white rounded-[2rem] p-8 flex flex-col relative overflow-hidden hover:-translate-y-2 hover:shadow-[0_30px_70px_rgba(0,0,0,0.35)] transition-all duration-300" data-testid={`category-card-${cat.id}`}>
                  <span className="absolute top-0 left-8 right-8 h-1 bg-saffron scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left rounded-b" aria-hidden />
                  <div className="flex items-center gap-4">
                    <span className="w-12 h-12 rounded-full bg-royal flex items-center justify-center shrink-0 transition-colors duration-300 group-hover:bg-saffron">
                      <Icon className="w-5 h-5 text-white transition-colors duration-300 group-hover:text-royal-deep" />
                    </span>
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-royal font-semibold">{cat.title}</p>
                  </div>
                  <h3 className="mt-4 font-display font-extrabold text-xl text-royal-deep leading-tight">{cat.tagline}</h3>
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {SERVICES.filter((s) => s.category === cat.id).map((s) => (
                      <li key={s.id}>
                        <Link to={s.slug} className="group/s flex items-center gap-2.5 text-sm text-ink-muted hover:text-royal transition-colors" data-testid={`category-service-${s.id}`}>
                          <Check className="w-4 h-4 text-saffron shrink-0" />
                          {s.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link to="/services" data-testid={`category-explore-${cat.id}`} className="mt-6 inline-flex items-center gap-2 font-bold text-sm text-royal hover:text-saffron transition-colors">
                    {cat.cta} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </ScrollSlideIn>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Who We Work With (photo cards) ---------------- */
const INDUSTRY_IMGS = {
  restaurants: IMG.interior,
  cafes: IMG.cafe,
  "hotels-resorts": IMG.hotel,
  "pubs-lounges": IMG.pub,
  "cloud-kitchens": IMG.kitchen,
  "event-venues": IMG.eventHall,
};

const IndustriesPreview = () => (
  <section className="hospitality-background py-12 sm:py-16 lg:py-20  relative overflow-hidden" data-testid="industries-preview-section">
    <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14">
      <FadeUp className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div>
          <Overline>Who We Work With</Overline>
          <h2 className="mt-4 font-display font-extrabold tracking-tight leading-[1.12] text-3xl sm:text-4xl lg:text-5xl text-royal-deep">
            Built for every kind of <span className="text-saffron">hospitality business</span>
          </h2>
        </div>
        <Link to="/industries" data-testid="industries-view-all-link" className="group inline-flex items-center gap-2 font-bold text-royal-deep hover:text-saffron transition-colors shrink-0">
          Explore industries
          <span className="w-9 h-9 rounded-full border border-royal/25 flex items-center justify-center group-hover:bg-saffron group-hover:border-saffron transition-all">
            <ArrowUpRight className="w-4 h-4" />
          </span>
        </Link>
      </FadeUp>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8 sm:mt-10">
        {INDUSTRIES.map((ind, i) => {
          const Icon = INDUSTRY_ICONS[ind.icon];
          return (
            <Slide key={ind.id} y={32} delay={i * 0.07}>
              <div className="hospitality-card industry-purple-card group h-full flex flex-col rounded-[1.75rem] border border-royal-light overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_28px_60px_rgba(59,35,115,0.16)] hover:border-royal/25 transition-all duration-300" data-testid={`industry-card-${ind.id}`}>
                <div className="relative h-44 overflow-hidden">
                  <img src={INDUSTRY_IMGS[ind.id]} alt={ind.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                  <span className="absolute bottom-4 left-4 w-11 h-11 rounded-full bg-royal border-2 border-white/30 flex items-center justify-center shadow-lg transition-colors duration-300 group-hover:bg-saffron">
                    <Icon className="w-5 h-5 text-white transition-colors duration-300 group-hover:text-royal-deep" />
                  </span>
                </div>
                <div className="p-6 flex flex-1 flex-col">
                  <h3 className="font-display font-bold text-lg text-white">{ind.title}</h3>
                  <p className="mt-2 text-[13px] text-white/85 leading-relaxed">{ind.desc}</p>
                  <Link to="/industries" className="mt-auto pt-5 inline-flex items-center gap-1.5 text-sm font-bold text-saffron-warm hover:text-white transition-colors" data-testid={`industry-explore-${ind.id}`}>
                    Explore <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </Slide>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------------- Why GrowMyRestro: text left, cards right ---------------- */
const WhyGMR = () => {
  const points = [
    { Icon: Hotel, title: "Hospitality Focused", desc: "We specialize in restaurants, cafés, hotels, resorts, pubs, cloud kitchens and hospitality businesses." },
    { Icon: Globe, title: "Online + Offline", desc: "We help your business grow digitally while also supporting your physical hospitality requirements." },
    { Icon: PhoneCall, title: "One Point of Contact", desc: "Reduce the hassle of managing multiple agencies, freelancers and vendors." },
    { Icon: Lightbulb, title: "Creative + Practical", desc: "We don't just create ideas. We help turn them into real business solutions." },
    { Icon: TrendingUp, title: "Growth Focused", desc: "Our goal is to improve your visibility, attract customers and build a stronger brand." },
  ];
  return (
    <section className="hospitality-background py-12 sm:py-16 lg:py-20  relative overflow-hidden" data-testid="why-gmr-section">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 grid lg:grid-cols-12 gap-8 lg:gap-12">
        <div className="lg:col-span-5">
          <Slide x={-40} y={0} className="lg:sticky lg:top-32">
            <Overline>Why GrowMyRestro</Overline>
            <h2 className="mt-4 font-display font-extrabold tracking-tight leading-[1.12] text-3xl sm:text-4xl lg:text-[2.9rem] text-royal-deep">
              One hospitality partner. <span className="text-saffron">Multiple solutions.</span>
            </h2>
            <p className="mt-5 text-ink-muted leading-relaxed">
              Most businesses have to coordinate with different agencies and vendors for marketing, websites,
              photography, advertising, events, uniforms and other requirements. GrowMyRestro brings them together.
            </p>
            <Link
              to="/contact"
              data-testid="why-consultation-button"
              className="mt-7 group inline-flex items-center gap-2 bg-royal text-white font-bold px-7 py-3.5 rounded-full hover:bg-saffron hover:text-royal-deep transition-colors duration-300"
            >
              Get a Free Consultation
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Slide>
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
          {points.map((p, i) => (
            <Slide key={p.title} y={30} delay={i * 0.09} className={i === 4 ? "sm:col-span-2" : ""}>
              <div className="hospitality-card group h-full bg-white rounded-3xl border border-line p-6 hover:border-royal/30 hover:shadow-[0_20px_50px_rgba(59,35,115,0.12)] hover:-translate-y-1 transition-all duration-300" data-testid={`why-point-${i}`}>
                <span className="w-11 h-11 rounded-full bg-royal-soft flex items-center justify-center transition-colors duration-300 group-hover:bg-saffron">
                  <p.Icon className="w-5 h-5 text-royal transition-colors duration-300 group-hover:text-royal-deep" />
                </span>
                <h3 className="mt-4 font-display font-bold text-lg text-royal-deep">{p.title}</h3>
                <p className="mt-2 text-[13px] text-ink-muted leading-relaxed">{p.desc}</p>
              </div>
            </Slide>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- How We Work ---------------- */
const WorkProcess = () => {
  const steps = [
    { title: "Understand", caption: "Your business. Your goals.", photo: IMG.meeting, alt: "Business owners discussing their goals" },
    { title: "Plan", caption: "Build your growth plan.", photo: "/photo-marketing.jpg", alt: "Planning a digital marketing strategy" },
    { title: "Create", caption: "Make your brand stand out.", photo: "/photo-video-shoot.jpg", alt: "A professional filming branded content" },
  ];
  return (
    <section className="hospitality-background py-12 sm:py-16 lg:py-20 relative overflow-hidden" data-testid="work-process-section">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14">
        <FadeUp className="max-w-3xl">
          <Overline>How We Work</Overline>
          <h2 className="mt-4 font-display font-extrabold tracking-tight leading-[1.12] text-3xl sm:text-4xl lg:text-5xl text-royal-deep">
            One clear process. <span className="text-saffron">Every single time.</span>
          </h2>
        </FadeUp>
        <ol className="growth-flow mt-8 sm:mt-10" data-testid="process-grid" aria-label="Our three-step growth process">
          {steps.map((s, i) => (
            <li key={s.title} className="process-step flow-step visual-process-step" tabIndex={0} data-testid={`process-node-${i}`}>
              <div className="process-photo">
                <img src={s.photo} alt={s.alt} loading="lazy" />
                <span className="process-number font-mono text-sm font-bold">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <div className="p-5">
                <h3 className="font-display font-bold text-xl">{s.title}</h3>
                <p className="mt-1 text-sm">{s.caption}</p>
              </div>
              {i < steps.length - 1 && <span className="flow-connector" aria-hidden="true" />}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

/* ---------------- Our Work preview ---------------- */
const WorkPreview = () => (
  <section className="hospitality-background py-12 sm:py-16 lg:py-20 relative overflow-hidden" data-testid="work-preview-section">
    <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14">
      <Slide y={24} className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
        <div className="max-w-2xl">
          <Overline>Our Work</Overline>
          <h2 className="mt-3 font-display font-extrabold tracking-tight leading-[1.12] text-3xl sm:text-4xl text-royal-deep">
            From ideas <span className="text-saffron">to execution</span>
          </h2>
          <p className="mt-3 text-ink-muted leading-relaxed text-sm sm:text-base">
            A glimpse of GrowMyRestro projects across marketing, content, branding, events and uniforms.
          </p>
        </div>
        <Link to="/work" data-testid="work-preview-button" className="group inline-flex self-start md:self-auto shrink-0 items-center gap-2 bg-royal text-white font-bold px-6 py-3 rounded-full hover:bg-saffron hover:text-royal-deep transition-colors duration-300">
          View Our Work <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Slide>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
        {WORK_PROJECTS.slice(0, 4).map((p, i) => (
          <Slide key={p.name} y={24} delay={i * 0.08}>
            <Link to="/work" className="group block relative rounded-[1.75rem] overflow-hidden aspect-[4/3] sm:aspect-[4/5] shadow-lg" data-testid={`work-preview-card-${i}`}>
              <WorkImage project={p} />
              <div className="absolute inset-0 bg-gradient-to-t from-royal-deep via-royal-deep/30 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-5">
                <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-saffron-warm font-semibold">{p.category}</p>
                <h3 className="mt-1 font-display font-bold text-lg text-white">{p.name}</h3>
              </div>
            </Link>
          </Slide>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Testimonials + FAQ side by side ---------------- */
const TESTIMONIALS = [
  { quote: "Our reels started pulling real views and weekend tables filled up within two months. The team handles everything — we just cook.", name: "Arjun R.", business: "Family Restaurant, Bengaluru", service: "Social Media + Meta Ads" },
  { quote: "They rebuilt our Google profile and Instagram from scratch. Party-hall enquiries now land on our WhatsApp every single week.", name: "Priya S.", business: "Rooftop Café, Mysuru", service: "Google Business Profile" },
  { quote: "Photos, ads, events, even staff uniforms — one team for everything. The monthly report is simple enough that I actually read it.", name: "Vikram D.", business: "Business Hotel, Hubballi", service: "360° Growth Plan" },
];

const TestimonialsFAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="hospitality-background py-12 sm:py-16 lg:py-20  relative overflow-hidden" data-testid="testimonials-faq-section">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
        <div data-testid="testimonials-section">
          <Slide x={-40} y={0}>
            <Overline>Client Testimonials</Overline>
            <h2 className="mt-4 font-display font-extrabold tracking-tight leading-[1.12] text-3xl sm:text-4xl text-royal-deep">
              Trusted by <span className="text-saffron">hospitality businesses</span>
            </h2>
            <div className="mt-4 inline-flex items-center gap-2.5 bg-royal-soft rounded-full px-4 py-2">
              <span className="flex gap-0.5 text-saffron">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-4 h-4" fill="currentColor" />
                ))}
              </span>
              <span className="text-sm font-bold text-royal-deep" data-testid="testimonials-rating">4.9 partner rating</span>
            </div>
          </Slide>
          <div className="mt-8 space-y-4">
            {TESTIMONIALS.map((t, i) => (
              <Slide key={t.name} x={-30} y={0} delay={i * 0.1}>
                <figure className="hospitality-card bg-white rounded-3xl border border-line p-6 hover:border-royal/25 hover:shadow-[0_18px_45px_rgba(59,35,115,0.1)] transition-all duration-300" data-testid={`testimonial-card-${i}`}>
                  <div className="flex gap-1 text-saffron">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <Star key={j} className="w-3.5 h-3.5" fill="currentColor" />
                    ))}
                  </div>
                  <blockquote className="mt-3 text-sm text-ink leading-relaxed">"{t.quote}"</blockquote>
                  <figcaption className="mt-4 pt-4 border-t border-line flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-royal text-white font-display font-bold text-sm flex items-center justify-center shrink-0">{t.name[0]}</span>
                    <span>
                      <span className="block font-bold text-royal-deep text-sm">{t.name}</span>
                      <span className="block text-xs text-ink-muted mt-0.5">{t.business} · {t.service}</span>
                    </span>
                  </figcaption>
                </figure>
              </Slide>
            ))}
          </div>
        </div>

        <div data-testid="faq-section">
          <Slide x={40} y={0}>
            <Overline>Frequently Asked Questions</Overline>
            <h2 className="mt-4 font-display font-extrabold tracking-tight leading-[1.12] text-3xl sm:text-4xl text-royal-deep">
              Questions? <span className="text-saffron">Answered.</span>
            </h2>
          </Slide>
          <div className="mt-8 space-y-3.5">
            {FAQS.map((f, i) => (
              <Slide key={f.q} x={30} y={0} delay={i * 0.05}>
                <div className="hospitality-card bg-white rounded-2xl border border-line overflow-hidden hover:border-royal/25 transition-colors" data-testid={`faq-item-${i}`}>
                  <button
                    onClick={() => setOpen(open === i ? -1 : i)}
                    data-testid={`faq-toggle-${i}`}
                    aria-expanded={open === i}
                    className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-4 text-left"
                  >
                    <span className="font-display font-bold text-[15px] text-royal-deep">{f.q}</span>
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${open === i ? "bg-saffron text-royal-deep rotate-180" : "bg-royal-soft text-royal"}`}>
                      <ChevronDown className="w-4 h-4" />
                    </span>
                  </button>
                  <div className={`grid transition-all duration-400 ${open === i ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                    <div className="overflow-hidden">
                      <p className="px-5 sm:px-6 pb-5 text-ink-muted leading-relaxed text-sm">{f.a}</p>
                    </div>
                  </div>
                </div>
              </Slide>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- Instagram strip ---------------- */
const InstaStrip = () => {
  const row = (key) => (
    <div key={key} className="flex shrink-0">
      {INSTA_POSTS.map((src, i) => (
        <a
          key={`${key}-${i}`}
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`insta-card-${key}-${i}`}
          className="group relative block w-44 sm:w-56 aspect-square m-3 rounded-3xl overflow-hidden shrink-0 border border-line"
        >
          <img src={src} alt="GrowMyRestro Instagram food content" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
          <span className="absolute inset-0 bg-royal-deep/0 group-hover:bg-royal-deep/50 transition-colors duration-300 flex items-center justify-center">
            <Instagram className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </span>
        </a>
      ))}
    </div>
  );
  return (
    <section className="hospitality-background py-12 sm:py-16 lg:py-20  relative overflow-hidden border-t border-line" data-testid="instagram-strip-section">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <FadeUp>
          <Overline>On Instagram</Overline>
          <h2 className="mt-4 font-display font-extrabold tracking-tight leading-[1.12] text-3xl sm:text-4xl text-royal-deep">
            Fresh from our <span className="text-saffron">content kitchen</span>
          </h2>
        </FadeUp>
        <FadeUp delay={0.1}>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-follow-button"
            className="group inline-flex items-center gap-2 font-bold text-royal-deep hover:text-saffron transition-colors"
          >
            @growmyrestrooffical
            <span className="w-9 h-9 rounded-full border border-royal/25 flex items-center justify-center group-hover:bg-saffron group-hover:border-saffron transition-all">
              <Instagram className="w-4 h-4" />
            </span>
          </a>
        </FadeUp>
      </div>
      <FadeUp delay={0.15} className="mt-10">
        <div className="overflow-hidden">
          <div className="flex w-max animate-marquee">
            {row("a")}
            {row("b")}
          </div>
        </div>
      </FadeUp>
    </section>
  );
};

/* ---------------- Final CTA (violet band) ---------------- */
const FinalCTA = () => (
  <section className="relative py-12 sm:py-16 lg:py-20 overflow-hidden bg-royal-deep" data-testid="cta-section">
    <div className="absolute -top-24 left-1/4 w-[26rem] h-[26rem] rounded-full bg-royal/50 blur-3xl" aria-hidden />
    <div className="absolute -bottom-32 right-10 w-96 h-96 rounded-full bg-saffron/20 blur-3xl" aria-hidden />
    <div className="relative max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 grid lg:grid-cols-2 gap-10 items-center">
      <Slide x={-40} y={0}>
        <h2 className="font-display font-extrabold tracking-tight leading-[1.1] text-3xl sm:text-4xl lg:text-[2.9rem] text-white">
          Everything your hospitality business needs. <span className="text-saffron">Under one roof.</span>
        </h2>
        <p className="mt-5 text-white/70 leading-relaxed max-w-lg">
          From getting discovered online to creating the experience customers remember — GrowMyRestro helps hospitality
          businesses grow. Digital growth. Creative solutions. Hospitality execution.
        </p>
      </Slide>
      <Slide x={40} y={0} className="lg:text-right">
        <p className="font-display font-bold text-xl text-white">Ready to grow your business?</p>
        <div className="mt-6 flex flex-wrap gap-4 lg:justify-end">
          <Link
            to="/contact"
            data-testid="cta-primary-button"
            className="group inline-flex items-center gap-2 bg-saffron text-royal-deep font-bold px-7 py-3.5 rounded-full hover:bg-white transition-colors duration-300"
          >
            Get a Free Consultation
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-whatsapp-button"
            className="inline-flex items-center gap-2 border-2 border-white/30 text-white font-bold px-7 py-3.5 rounded-full hover:border-saffron hover:text-saffron-warm transition-colors duration-300"
          >
            WhatsApp Us
          </a>
        </div>
      </Slide>
    </div>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <IconStrip />
      <MoreThanMarketing />
      <WhatWeDo />
      <IndustriesPreview />
      <WhyGMR />
      <WorkProcess />
      <WorkPreview />
      <TestimonialsFAQ />
      <InstaStrip />
      <FinalCTA />
    </main>
  );
}
